import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import DynamicRequestWizard from './DynamicRequestWizard';
import RequestWizard from './RequestWizard';
import CountryPackageSelector from './CountryPackageSelector';

interface Package {
  id: number;
  name: string;
  price: number;
  currency: string;
  features: string[];
  popular: boolean;
  country_name: string;
  country_flag: string;
}

const SmartRequestWizard: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [hasTemplate, setHasTemplate] = useState<boolean | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);

  useEffect(() => {
    fetchPackages();
  }, []);

  // Handle preselected package from navigation state
  useEffect(() => {
    const locationState = location.state as { selectedPackage?: any } | null;
    const preselectedPkg = locationState?.selectedPackage;

    if (preselectedPkg && packages.length > 0 && !selectedPackage) {
      // Find the package in our packages list to ensure it's valid
      const preselectedPackage = packages.find(p => p.id === preselectedPkg.id);
      if (preselectedPackage) {
        setSelectedPackage(preselectedPackage);
      } else {
        // If the package isn't found in our list, use the preselected data but convert it to our format
        const convertedPackage: Package = {
          id: preselectedPkg.id,
          name: preselectedPkg.name,
          price: preselectedPkg.price,
          currency: preselectedPkg.currency,
          features: preselectedPkg.features || [],
          popular: preselectedPkg.popular || false,
          country_name: preselectedPkg.country?.name || preselectedPkg.country_name || '',
          country_flag: preselectedPkg.country?.flag || preselectedPkg.country_flag || '🌍'
        };
        setSelectedPackage(convertedPackage);
      }

      // Clear the location state to prevent reprocessing on navigation
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [packages, location.state, selectedPackage]);

  useEffect(() => {
    if (selectedPackage) {
      checkPackageTemplate();
    }
  }, [selectedPackage]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPackages();
      setPackages(response.packages || []);
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const checkPackageTemplate = async () => {
    if (!selectedPackage) return;

    try {
      setLoading(true);
      const response = await apiService.getPackageTemplate(selectedPackage.id);
      setHasTemplate(response.success && response.has_template && !!response.template);
    } catch (error) {      setHasTemplate(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = (packageId: number) => {
    const selected = packages.find(p => p.id === packageId);
    setSelectedPackage(selected || null);
  };

  const handleBackToSelection = () => {
    setSelectedPackage(null);
    setHasTemplate(null);
  };

  if (loading && packages.length === 0) {
    return <LoadingSpinner />;
  }

  // Package selection step
  if (!selectedPackage) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('requestWizard.select_package')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('requestWizard.choose_package_description')}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <CountryPackageSelector
            packages={packages}
            selectedPackageId={0}
            onPackageSelect={handlePackageSelect}
          />
        )}
      </div>
    );
  }

  // Loading template check
  if (hasTemplate === null) {
    return <LoadingSpinner />;
  }

  // Render appropriate wizard based on template availability
  return (
    <div>
      {/* Back to package selection */}
      <div className="max-w-4xl mx-auto p-6 pb-0">
        <button
          onClick={handleBackToSelection}
          className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('requestWizard.back_to_packages')}
        </button>
      </div>

      {hasTemplate ? (
        <DynamicRequestWizard selectedPackage={selectedPackage} />
      ) : (
        <RequestWizard />
      )}
    </div>
  );
};

export default SmartRequestWizard;